import type { ReactNode, FC, PropsWithChildren } from 'react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppUpdated,
  isChunkLoadError,
  SomethingWentWrong,
} from '@pflegenavi/web-components';
import * as Sentry from '@sentry/react';

interface Props extends PropsWithChildren {
  children?: ReactNode;
  home: string;
  supportEmail: string;
}

// Always returns a string and never throws, so a non-Error throw value (e.g. a
// circular object, which would make JSON.stringify throw) can't crash the error
// boundary's own fallback render.
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  try {
    return JSON.stringify(error) ?? String(error);
  } catch {
    return String(error);
  }
};
export const InnerErrorBoundary: FC<Props> = ({
  children,
  home,
  supportEmail,
}) => {
  const navigate = useNavigate();

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError, error }) => {
        if (isChunkLoadError(error)) {
          return <AppUpdated />;
        }

        return (
          <SomethingWentWrong
            handleGoHomeClick={() => {
              resetError();
              navigate(home);
            }}
            supportEmail={supportEmail}
            errorMessage={getErrorMessage(error)}
          />
        );
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
